/**
 * Market hours utility functions for trading operations
 */

/**
 * Returns the current time broken down in the America/New_York timezone.
 * @returns {{ easternTime: Date, day: number, hour: number, minute: number }}
 */
const getEasternTimeComponents = () => {
  const now = new Date();
  const easternTime = new Date(now.toLocaleString("en-US", { timeZone: "America/New_York" }));
  return {
    easternTime,
    day: easternTime.getDay(),    // 0 = Sunday, 6 = Saturday
    hour: easternTime.getHours(),
    minute: easternTime.getMinutes(),
  };
};

/**
 * Returns true on Saturday (6) or Sunday (0) in Eastern Time.
 */
export const isWeekend = () => {
  const { day } = getEasternTimeComponents();
  return day === 0 || day === 6;
};

/**
 * Check if we are currently after regular trading hours (after 4:00 PM Eastern)
 * NOTE: returns false on weekends — use isWeekend() separately for full session detection.
 * @returns {boolean} true if after regular trading hours on a weekday
 */
export const isAfterRegularTradingHours = () => {
  const { day, hour, minute } = getEasternTimeComponents();

  // Weekends are neither "regular" nor "after regular" — handled by isWeekend()
  if (day === 0 || day === 6) return false;

  // Regular trading hours end at 4:00 PM Eastern (16:00)
  return hour > 16 || (hour === 16 && minute > 0);
};

/**
 * Check if we are currently during regular trading hours (9:30 AM - 4:00 PM Eastern)
 * @returns {boolean} true if during regular trading hours
 */
export const isDuringRegularTradingHours = () => {
  const { day, hour, minute } = getEasternTimeComponents();

  if (day === 0 || day === 6) return false;

  // Regular trading hours: 9:30 AM - 4:00 PM Eastern
  const isAfter930AM = hour > 9 || (hour === 9 && minute >= 30);
  const isBefore4PM = hour < 16 || (hour === 16 && minute === 0);

  return isAfter930AM && isBefore4PM;
};

/**
 * Get the appropriate time-in-force for closing positions based on current time.
 * - Regular weekday session  → DAY
 * - After-hours weekday      → DYP (Day Plus / extended hours)
 * - Weekend                  → GCP (GTC Extended Hours — rests through weekend, fills at next ETH open)
 */
export const getClosePositionTimeInForce = () => {
  if (isWeekend()) return 'GCP';
  return isAfterRegularTradingHours() ? 'DYP' : 'DAY';
};

/**
 * Get the appropriate order type for closing positions based on current time.
 * - Regular weekday session → Market
 * - After-hours or weekend  → Limit (required when TIF is DYP / GCP)
 */
export const getClosePositionOrderType = () => {
  return (isAfterRegularTradingHours() || isWeekend()) ? 'Limit' : 'Market';
};

/**
 * Get the appropriate limit price for after-hours / weekend closing.
 * @param {object} position - Position object with Last or MarkToMarketPrice
 * @returns {number|null} Limit price, or null during regular market hours
 */
export const getClosePositionLimitPrice = (position) => {
  if (!isAfterRegularTradingHours() && !isWeekend()) {
    return null; // Market order during regular hours — no limit price needed
  }

  const currentPrice = parseFloat(position.Last) || parseFloat(position.MarkToMarketPrice) || 0;
  return currentPrice;
};

// Extended-hours durations have a regular-session equivalent we can fall back to.
const REGULAR_SESSION_EQUIVALENT = {
  DYP: 'DAY',
  GCP: 'GTC',
  GDP: 'GTD',
  FOK: 'DAY',
};

/**
 * Order types that can carry an extended-hours duration. Unpriced orders (Market,
 * StopMarket) cannot: TradeStation rejects them with "No market or stop orders with
 * TIF of 'Day+','GTC+','GTD+', or 'Fill-or-Kill'".
 */
export const supportsExtendedHoursTimeInForce = (orderType) => {
  const normalized = String(orderType || '').toLowerCase().replace(/[\s_]/g, '');
  return normalized === 'limit' || normalized === 'stoplimit';
};

/**
 * Coerce a user-selected duration to one the given order type accepts.
 * Limit / StopLimit keep the duration as-is; Market / StopMarket fall back to the
 * regular-session equivalent (DYP → DAY, GCP → GTC).
 */
export const resolveTimeInForceForOrderType = (orderType, duration) => {
  if (!duration || supportsExtendedHoursTimeInForce(orderType)) return duration;
  return REGULAR_SESSION_EQUIVALENT[String(duration).toUpperCase()] || duration;
};

/**
 * Duration to preselect for a new order, based on the current session and order type.
 * Pre-market counts as extended hours here, unlike isAfterRegularTradingHours().
 * @param {string} orderType - e.g. 'market', 'limit', 'StopLimit'
 * @param {{ allowExtendedHours?: boolean }} [options] - futures accounts pass false
 */
export const getDefaultTimeInForce = (orderType, { allowExtendedHours = true } = {}) => {
  const session = isWeekend() ? 'GCP' : (isDuringRegularTradingHours() ? 'DAY' : 'DYP');
  const duration = allowExtendedHours ? session : (REGULAR_SESSION_EQUIVALENT[session] || session);
  return resolveTimeInForceForOrderType(orderType, duration);
};

